<template>
  <b-modal
    id="subscription-qrcode-manager-modal"
    ref="modal"
    title="Subscription QR Code Manager"
    size="xl"
    hide-footer
    @show="onShow"
  >
    <div class="container-fluid py-3">

      <!-- Page header -->
      <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
        <div class="mb-2 mb-md-0">
          <h5 class="mb-0 font-weight-bold">Check-in QR Codes</h5>
          <small class="text-muted">Manage access QR codes for each entrance</small>
        </div>
        <b-button variant="primary" size="sm" @click="openCreateModal">
          <i class="fas fa-plus mr-1"></i> New QR Code
        </b-button>
      </div>

      <!-- Summary strip -->
      <b-row class="mb-3" v-if="summary">
        <b-col md="4" sm="6" class="mb-2">
          <b-card class="text-center border-0 bg-light">
            <h3 class="font-weight-bold text-primary mb-0">{{ summary.today_entries }}</h3>
            <p class="text-muted mb-0">Today's Check-ins</p>
          </b-card>
        </b-col>
        <b-col md="4" sm="6" class="mb-2">
          <b-card class="text-center border-0 bg-light">
            <h3 class="font-weight-bold mb-0">{{ summary.subscription }}</h3>
            <p class="text-muted mb-0">Subscription (today)</p>
          </b-card>
        </b-col>
        <b-col md="4" sm="6" class="mb-2">
          <b-card class="text-center border-0 bg-light">
            <h3 class="font-weight-bold text-secondary mb-0">{{ summary.wallet }}</h3>
            <p class="text-muted mb-0">Wallet (today)</p>
          </b-card>
        </b-col>
      </b-row>

      <!-- QR Code list -->
      <b-card class="mb-4">
        <template #header>
          <span class="font-weight-bold">Active QR Codes</span>
        </template>

        <div v-if="loadingQr" class="text-center py-4">
          <b-spinner variant="secondary" />
        </div>

        <div v-else-if="qrCodes.length === 0" class="text-center py-4 text-muted">
          <p class="mb-0">No QR codes yet. Create one to get started.</p>
        </div>

        <b-table
          stacked="md"
          v-else
          :items="qrCodes"
          :fields="qrFields"
          responsive
          hover
          class="mb-0"
        >
          <template #cell(label)="data">
            <span class="font-weight-bold">{{ data.item.label || '—' }}</span>
          </template>

          <template #cell(is_active)="data">
            <b-badge :variant="data.item.is_active ? 'success' : 'secondary'">
              {{ data.item.is_active ? 'Active' : 'Inactive' }}
            </b-badge>
          </template>

          <template #cell(inside_now)="data">
            <span class="font-weight-bold">{{ data.item.inside_now }}</span>
          </template>

          <template #cell(url)="data">
            <a
              v-if="data.item.url"
              :href="data.item.url"
              target="_blank"
              class="text-muted text-truncate d-inline-block"
              style="max-width:200px;"
            >
              {{ data.item.url }}
            </a>
            <span v-else class="text-muted">—</span>
          </template>

          <!-- Three-dots dropdown -->
          <!-- Three-dots dropdown using b-dropdown so it works inside b-modal -->
          <template #cell(actions)="data">
            <b-dropdown
              size="sm"
              variant="link"
              toggle-class="text-secondary p-0"
              no-caret
              right
            >
              <template #button-content>
                <i class="fas fa-ellipsis-v"></i>
              </template>

              <b-dropdown-item class="m-0" @click="viewQr(data.item)">
                View QR Code
              </b-dropdown-item>
              <b-dropdown-item class="m-0" v-if="data.item.qr_file" @click="downloadQr(data.item)">
                Download QR
              </b-dropdown-item>
              <b-dropdown-item class="m-0" @click="printQrItem(data.item)">
                Print QR
              </b-dropdown-item>

              <b-dropdown-item class="m-0" @click="toggleActive(data.item)">
                {{ data.item.is_active ? 'Deactivate' : 'Activate' }}
              </b-dropdown-item>
              <b-dropdown-item class="m-0" @click="confirmRegenerate(data.item)">
                Regenerate Token
              </b-dropdown-item>

              <b-dropdown-divider />

              <b-dropdown-item class="m-0" @click="confirmDelete(data.item)" link-class="text-danger">
                Delete
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
      </b-card>

      <!-- Attendance log -->
      <b-card>
        <template #header>
          <div class="d-flex flex-wrap justify-content-between align-items-center">
            <span class="font-weight-bold mb-1">Attendance Log</span>
            <div class="d-flex flex-wrap">
              <date-range-picker :auto-apply="true" :ranges="false" :append-to-body="true" v-model="dateRangePicker" class="mr-1 mb-1" style="width:220px;max-width:100%;">
                <template #input="picker">
                  <span v-if="picker.startDate && picker.endDate">{{ formatPickerDate(picker.startDate) }} - {{ formatPickerDate(picker.endDate) }}</span>
                  <span v-else class="text-muted small"><i class="bi bi-calendar-event mr-1"></i>Date range</span>
                </template>
              </date-range-picker>
              <b-form-select
                v-model="filters.check_in_type"
                :options="checkinTypeOptions"
                size="sm"
                class="mr-1 mb-1"
                style="width:150px;"
              />
              <b-button size="sm" variant="outline-secondary" @click="loadAttendances">
                Filter
              </b-button>
            </div>
          </div>
        </template>

        <div v-if="loadingAttendances" class="text-center py-4">
          <b-spinner variant="secondary" />
        </div>

        <div v-else-if="!attendances.data || attendances.data.length === 0"
             class="text-center py-4 text-muted">
          No attendance records found.
        </div>

        <b-table
          stacked="md"
          v-else
          :items="attendances.data"
          :fields="attFields"
          responsive
          hover
          class="mb-0"
        >
          <template #cell(checked_in_at)="data">
            <span class="text-muted">{{ formatDate(data.item.checked_in_at) }}</span>
          </template>

          <template #cell(customer)="data">
            <div class="font-weight-bold">{{ data.item.customer && data.item.customer.full_name || '—' }}</div>
            <div class="text-muted" style="font-size:11px;">{{ data.item.customer && data.item.customer.phone }}</div>
          </template>

          <template #cell(package)="data">
            {{ data.item.package && data.item.package.name || '—' }}
          </template>

          <template #cell(qr_label)="data">
            <span class="text-muted">{{ data.item.qr_code && data.item.qr_code.label || '—' }}</span>
          </template>

          <template #cell(check_in_type)="data">
            <b-badge :variant="data.item.check_in_type === 'subscription' ? 'success' : 'secondary'">
              {{ data.item.check_in_type }}
            </b-badge>
          </template>

          <template #cell(amount_deducted)="data">
            <span v-if="data.item.amount_deducted" class="font-weight-bold">
              KES {{ formatAmount(data.item.amount_deducted) }}
            </span>
            <span v-else class="text-muted">—</span>
          </template>
        </b-table>

        <!-- Pagination -->
        <div v-if="attendances.last_page > 1"
             class="d-flex flex-wrap justify-content-between align-items-center pt-2">
          <small class="text-muted">
            Showing {{ attendances.from }}–{{ attendances.to }} of {{ attendances.total }}
          </small>
          <b-pagination
            v-model="filters.page"
            :total-rows="attendances.total"
            :per-page="attendances.per_page"
            size="sm"
            class="mb-0"
            @change="loadAttendances"
          />
        </div>
      </b-card>

      <!-- ── CREATE MODAL ── -->
      <b-modal
        ref="createModal"
        title="Create QR Code"
        ok-title="Create"
        ok-variant="primary"
        cancel-variant="outline-secondary"
        @ok="handleCreate"
        @hidden="form = { label: '', location_tag: '' }"
      >
        <b-form-group
          label="Label / Location"
          description="Helps staff identify which door or area this QR code is for."
        >
          <b-form-input
            v-model="form.label"
            placeholder="e.g. Main Entrance, Pool Door, Gym Floor"
            :state="formErrors.label ? false : null"
          />
          <b-form-invalid-feedback>{{ formErrors.label }}</b-form-invalid-feedback>
        </b-form-group>
      </b-modal>

      <!-- ── VIEW / PRINT QR MODAL ── -->
      <b-modal
        ref="qrModal"
        :title="activeQr ? 'QR Code — ' + activeQr.label : 'QR Code'"
        hide-footer
        size="md"
        centered
      >
        <div v-if="activeQr" class="text-center p-2">

          <!-- QR image from backend URL -->
          <div v-if="activeQr.qr_file" class="mb-3">
            <img
              :src="activeQr.qr_file"
              :alt="'QR code for ' + activeQr.label"
              class="img-fluid border rounded"
              

              @error="onQrImageError"
            />
          </div>
          <div v-else class="py-4 text-muted">
            <p class="mb-0">QR image not yet generated.</p>
            {{ activeQr.qr_file }}
          </div>

          <p class="font-weight-bold mb-1">{{ activeQr.label }}</p>
          <a
            v-if="activeQr.url"
            :href="activeQr.url"
            target="_blank"
            class="text-muted text-truncate d-block mb-3"
            style="max-width:240px; margin:0 auto;"
          >
            {{ activeQr.url }}
          </a>

          <div class="d-flex justify-content-center">
            <b-button
              v-if="activeQr.qr_file"
              variant="primary"
              size="sm"
              class="mr-2"
              @click="printQrItem(activeQr)"
            >
              Print
            </b-button>
            <b-button
              v-if="activeQr.qr_file"
              variant="outline-secondary"
              size="sm"
              class="mr-2"
              @click="downloadQr(activeQr)"
            >
              Download
            </b-button>
            <b-button variant="outline-secondary" size="sm" @click="$refs.qrModal.hide()">
              Close
            </b-button>
          </div>
        </div>
      </b-modal>

    </div>
  </b-modal>
</template>

<script>
import moment from 'moment'
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker'

export default {
  name: 'SubscriptionQrCodeManager',
  components: { DateRangePicker },

  data() {
    return {
      qrCodes:            [],
      summary:            null,
      attendances:        {},
      loadingQr:          false,
      loadingAttendances: false,
      activeQr:           null,

      filters: {
        date_from:      '',
        date_to:        '',
        check_in_type:  '',
        page:           1,
      },

      form: {
        label:        '',
        location_tag: '',
      },

      formErrors: {},

      checkinTypeOptions: [
        { value: '',             text: 'All types' },
        { value: 'subscription', text: 'Subscription' },
        { value: 'wallet',       text: 'Wallet' },
      ],

      qrFields: [
        { key: 'label',      label: 'Label' },
        { key: 'is_active',  label: 'Status',    class: 'text-center' },
        { key: 'inside_now', label: 'Inside Now', class: 'text-center' },
        { key: 'url',        label: 'Check-in URL' },
        { key: 'actions',    label: '',           class: 'text-right' },
      ],

      attFields: [
        { key: 'checked_in_at',  label: 'When' },
        { key: 'customer',       label: 'Customer' },
        { key: 'package',        label: 'Package' },
        { key: 'qr_label',       label: 'Entrance' },
        { key: 'check_in_type',  label: 'Type',     class: 'text-center' },
        { key: 'amount_deducted', label: 'Deducted', class: 'text-right' },
      ],
    }
  },

  computed: {
    dateRangePicker: {
      get() {
        return {
          startDate: this.filters.date_from ? new Date(this.filters.date_from) : null,
          endDate: this.filters.date_to ? new Date(this.filters.date_to) : null,
        };
      },
      set(val) {
        this.filters.date_from = val && val.startDate ? moment(val.startDate).format('YYYY-MM-DD') : '';
        this.filters.date_to = val && val.endDate ? moment(val.endDate).format('YYYY-MM-DD') : '';
      },
    },
  },

  methods: {
    formatPickerDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },

    show() {
      this.$refs.modal.show()
    },

    async onShow() {
      this.loadQrCodes()
      this.loadSummary()
      this.loadAttendances()
    },

    // ── Data loading ──────────────────────────────────────────────────────

    async loadQrCodes() {
      this.loadingQr = true
      try {
        const { data } = await axios.get('/api/subscriptions/checkin/qr-codes')
        this.qrCodes = data
      } catch {
        this.toast('Failed to load QR codes', 'danger')
      } finally {
        this.loadingQr = false
      }
    },

    async loadSummary() {
      try {
        const { data } = await axios.get('/api/subscriptions/checkin/summary')
        this.summary = data
      } catch { /* non-critical */ }
    },

    async loadAttendances() {
      this.loadingAttendances = true
      try {
        const { data } = await axios.get('/api/subscriptions/checkin/attendances', {
          params: { ...this.filters },
        })
        this.attendances = data.attendances
        if (data.summary) this.summary = data.summary
      } catch {
        this.toast('Failed to load attendances', 'danger')
      } finally {
        this.loadingAttendances = false
      }
    },

    // ── Create QR ─────────────────────────────────────────────────────────

    openCreateModal() {
      this.form       = { label: '', location_tag: '' }
      this.formErrors = {}
      this.$refs.createModal.show()
    },

    async handleCreate(bvModalEvent) {
      bvModalEvent.preventDefault()
      this.formErrors = {}

      if (!this.form.label.trim()) {
        this.formErrors.label = 'Please enter a label or location.'
        return
      }

      this.form.location_tag = this.form.label.trim().toLowerCase()

      try {
        await axios.post('/api/subscriptions/checkin/qr-codes', this.form)
        this.toast('QR code created successfully.', 'success')
        this.$refs.createModal.hide()
        this.loadQrCodes()
      } catch (e) {
        this.toast(e.response?.data?.message || 'Failed to create QR code.', 'danger')
      }
    },

    // ── Toggle active ─────────────────────────────────────────────────────

    async toggleActive(qr) {
      try {
        await axios.put(`/api/subscriptions/checkin/qr-codes/${qr.id}`, {
          is_active: !qr.is_active,
        })
        this.toast('Status updated.', 'success')
        this.loadQrCodes()
      } catch {
        this.toast('Failed to update status.', 'danger')
      }
    },

    // ── Regenerate token ──────────────────────────────────────────────────

    async confirmRegenerate(qr) {
      const ok = await this.$bvModal.msgBoxConfirm(
        'This will invalidate the existing printed QR code. You must reprint it. Continue?',
        { title: 'Regenerate Token', okTitle: 'Regenerate', okVariant: 'primary',
          cancelVariant: 'outline-secondary' }
      )
      if (!ok) return
      try {
        await axios.post(`/api/subscriptions/checkin/qr-codes/${qr.id}/regenerate`)
        this.toast('Token regenerated. Please reprint the QR code.', 'success')
        this.loadQrCodes()
      } catch {
        this.toast('Failed to regenerate.', 'danger')
      }
    },

    // ── Delete ────────────────────────────────────────────────────────────

    async confirmDelete(qr) {
      const ok = await this.$bvModal.msgBoxConfirm(
        'Delete this QR code? Attendance records will be preserved.',
        { title: 'Delete QR Code', okTitle: 'Delete', okVariant: 'danger',
          cancelVariant: 'outline-secondary' }
      )
      if (!ok) return
      try {
        await axios.delete(`/api/subscriptions/checkin/qr-codes/${qr.id}`)
        this.toast('QR code deleted.', 'success')
        this.loadQrCodes()
      } catch {
        this.toast('Failed to delete.', 'danger')
      }
    },

    // ── View QR in modal ──────────────────────────────────────────────────

    viewQr(qr) {
      this.activeQr = qr
      this.$refs.qrModal.show()
    },

    // ── Download QR PNG ───────────────────────────────────────────────────
    // Fetches the PNG from the backend CDN URL and triggers a browser download.

    async downloadQr(qr) {
      if (!qr.qr_file) {
        this.toast('No QR image available for this code.', 'danger')
        return
      }

      try {
        const response = await fetch( qr.qr_file)
        if (!response.ok) throw new Error('Failed to fetch QR image.')

        const blob = await response.blob()
        const url  = window.URL.createObjectURL(blob)
        const a    = document.createElement('a')
        a.href     = url
        a.download = `qr-${qr.label || qr.id}.png`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      } catch {
        // CORS fallback — open directly in new tab so the user can save manually
        window.open( qr.qr_file, '_blank')
        this.toast('Download opened in new tab — right-click the image to save it.', 'info')
      }
    },

    // ── Print QR ─────────────────────────────────────────────────────────

    printQrItem(qr) {
      if (! qr.qr_file) {
        this.toast('No QR image available to print.', 'danger')
        return
      }

      const pw = window.open('', '_blank')
      pw.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>QR Code — ${this.escHtml(qr.label || '')}</title>
  <style>
    body { font-family: Arial, sans-serif; text-align: center; padding: 40px; }
    img  { width: 260px; height: 260px; }
    h2   { font-size: 20px; margin-bottom: 8px; }
    p    { font-size: 13px; color: #666; margin: 4px 0; }
    .url { font-size: 10px; color: #999; word-break: break-all; margin-top: 12px; }
    @media print { @page { size: A5; margin: 20mm; } }
  </style>
</head>
<body>
  <h2>${this.escHtml(qr.label || 'Entrance')}</h2>
  <img src="${this.escHtml( qr.qr_file)}" alt="QR Code" />
  ${qr.url ? `<p class="url">${this.escHtml(qr.url)}</p>` : ''}
</body>
</html>`)
      pw.document.close()
      setTimeout(() => { pw.focus(); pw.print(); pw.close() }, 400)
    },

    // ── Helpers ───────────────────────────────────────────────────────────

    onQrImageError(e) {
      e.target.style.display = 'none'
    },

    formatDate(d) {
      return d ? new Date(d).toLocaleString('en-KE') : '—'
    },

    formatAmount(v) {
      return parseFloat(v || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 })
    },

    escHtml(str) {
      return String(str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    },

    toast(message, variant = 'info') {
      this.$bvToast?.toast(message, {
        title:   variant === 'danger' ? 'Error' : 'Notice',
        variant,
      })
    },
  },
}
</script>
